import { c as _c } from "react/compiler-runtime";
import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import type { MenuItem as MenuItemType } from "@/types";
import { Link } from "@/components/ui/Link";
import { SubMenuTrigger } from "./SubMenuTrigger";
export const menuItemVariants = cva("text-white px-0 lg:px-md h-full rounded-full hover:text-white cursor-pointer whitespace-nowrap transition-all duration-200 outline-offset-0 focus-visible:outline-2 overflow-y-hidden", {
  variants: {
    variant: {
      dark: "aria-[current=page]:bg-purple-dark md:hover:bg-transparent md:aria-[current=page]:bg-transparent outline-purple",
      light: "aria-[current=page]:bg-black/40 md:hover:bg-transparent md:aria-[current=page]:bg-transparent outline-yellow"
    },
    type: {
      logo: "w-32",
      link: "",
      submenu: ""
    }
  },
  defaultVariants: {
    variant: "dark"
  }
});
export interface MenuItemProps extends VariantProps<typeof menuItemVariants> {
  /** Menu item data (either link or submenu) */
  item: MenuItemType;
  /** Whether the menu item is currently active */
  isActive?: boolean;
  /** Optional className for styling */
  className?: string;
  /** Optional children for custom content (like the logo icon) */
  children?: React.ReactNode;
  /** Called when the mouse enters this item (used by the sliding pill in Menu) */
  onMouseEnter?: () => void;
  onFocus?: () => void;
}

/**
 * Menu item component that renders either a link or a sub-menu.
 * Forwards its ref to the underlying <li> element so the parent Menu
 * can read offsetLeft / offsetWidth for the sliding-pill animation.
 */
export const MenuItem = React.forwardRef<HTMLLIElement, MenuItemProps>((t0, ref) => {
  const $ = _c(13);
  const {
    item,
    variant,
    type,
    isActive,
    className: t1,
    children,
    onMouseEnter,
    onFocus
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t2 = `relative ${className}`;
  let t3;
  if ($[0] !== children || $[1] !== isActive || $[2] !== item.data || $[3] !== item.type || $[4] !== type || $[5] !== variant) {
    t3 = item.type === "link" ? <Link {...item.data} isActive={isActive} className={menuItemVariants({
      variant,
      type
    })}>{children}</Link> : <SubMenuTrigger data={item.data} variant={variant} isActive={isActive} />;
    $[0] = children;
    $[1] = isActive;
    $[2] = item.data;
    $[3] = item.type;
    $[4] = type;
    $[5] = variant;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onFocus || $[8] !== onMouseEnter || $[9] !== ref || $[10] !== t2 || $[11] !== t3) {
    t4 = <li ref={ref} className={t2} onMouseEnter={onMouseEnter} onFocus={onFocus}>{t3}</li>;
    $[7] = onFocus;
    $[8] = onMouseEnter;
    $[9] = ref;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
});
MenuItem.displayName = "MenuItem";